/**
 * Send Quote Utility
 * Handles sending a draft quote by creating a hold (if applicable) and updating the quote to pending status
 */

import { callServerAction } from '@/util/functions/fetching/callServerAction';
import { completeDraft } from './completeDraft';
import { Event, Quote } from '@/util/types/serverTypes';
import { Dayjs } from 'dayjs';
import { addToast } from '@heroui/react';
import { AddressForScheduler } from '@/util/types/globalTypes';
import {
  ContactType,
  ContactRoleType,
  RequiredInfoValue,
  EquipmentQuantity,
} from '@/util/types/serverTypes';
import { ScheduleSettingsType } from '@/util/types/serverTypeCollection/scheduleSettings';
import { DiscountCode } from '@/util/types/serverTypeCollection/discounts';
import { Bundle } from '@/util/types/serverTypeCollection/discounts';
import { generateAccessToken } from '@/util/functions/auth/tokenUtils';

type SendQuoteParams = {
  // Draft quote info
  draftQuoteId: string;
  draftQuoteSlug: string | null;

  // Quote data
  address: AddressForScheduler;
  priceData: {
    totalPrice: number;
    totalDuration: number;
  };
  contact: ContactType | null;
  selectedRole: ContactRoleType | null;
  additionalContacts: Array<{
    _contactId: ContactType;
    _roleId: string;
  }>;
  requiredInfoValues: RequiredInfoValue[];
  equipmentQuantities: EquipmentQuantity[];

  // Hold data
  selectedDateTime: string | null;
  selectedSlotInspectorInfo: {
    _id: string;
    firstName: string;
    lastName: string;
  } | null;
  // If length === 1, user specifically requested this inspector
  selectedInspectorIdsForFilter?: string[];
  scheduleSettings: ScheduleSettingsType;

  // Bundle and discount
  selectedBundle: string | null;
  bundles: Bundle[];
  appliedDiscountCode: DiscountCode | null;
  appliedDiscountServiceIds: string[];

  // Working hold cleanup
  workingHold: Event | null;

  // Hold creation control
  createHold?: boolean; // If false, won't create a quote hold event (but will still save datetime/inspector)

  // WhatConverts integration
  wcLeadId?: string;

  // Auth
  jwtToken: string;
  companyId: string;

  // Timezone
  tzDayjs: (date?: string | Date) => Dayjs;

  // State setters
  setDraftQuoteId: (id: string | null) => void;
  setDraftQuoteSlug: (slug: string | null) => void;
  setWorkingHold: (hold: Event | null) => void;
  setWorkingHoldExpiresAt: (expiresAt: Dayjs | null) => void;
};

/**
 * Sends a draft quote by:
 * 1. Creating a hold event (if slot is selected)
 * 2. Updating the draft quote to pending status with hold information
 * 3. Cleaning up working holds and draft storage
 *
 * @param params - All required data for sending the quote
 * @returns Promise that resolves when quote is sent successfully
 */
export async function sendQuote(params: SendQuoteParams): Promise<Quote> {
  const {
    draftQuoteId,
    draftQuoteSlug,
    address,
    priceData,
    contact,
    selectedRole,
    additionalContacts,
    requiredInfoValues,
    equipmentQuantities,
    selectedDateTime,
    selectedSlotInspectorInfo,
    selectedInspectorIdsForFilter,
    scheduleSettings,
    selectedBundle,
    bundles,
    appliedDiscountCode,
    appliedDiscountServiceIds,
    workingHold,
    createHold = true,
    wcLeadId,
    jwtToken,
    companyId,
    tzDayjs,
    setDraftQuoteId,
    setDraftQuoteSlug,
    setWorkingHold,
    setWorkingHoldExpiresAt,
  } = params;

  // Validate required fields
  if (!draftQuoteId) {
    addToast({
      title: 'Error',
      description: 'No quote found. Please start a new quote.',
      color: 'danger',
    });
    throw new Error('No quote found');
  }

  if (!address || !priceData) {
    addToast({
      title: 'Error',
      description: 'Please complete all required fields before sending the quote.',
      color: 'danger',
    });
    throw new Error('Missing required fields');
  }

  let holdEventId: number | undefined = undefined;
  let finalHoldUntil: string | undefined = undefined;

  try {
    // HANDLE THE HOLD
    // Create hold if a slot is selected AND createHold is enabled
    if (createHold && selectedDateTime && selectedSlotInspectorInfo) {
      const holdDurationHours = scheduleSettings?.defaultHoldDurationForQuoteInHours || 24;
      const holdUntil = tzDayjs().add(holdDurationHours, 'hour');
      finalHoldUntil = holdUntil.format();

      const eventEndTime = tzDayjs(selectedDateTime).add(priceData.totalDuration, 'hour').format();

      const expiresInMinutes = holdUntil.diff(tzDayjs(), 'minute');
      const validExpiresInMinutes = expiresInMinutes > 0 ? expiresInMinutes : 0;

      // Create new hold event
      const eventData: Omit<Event, '_id'> = {
        type: 'Block',
        _companyId: companyId,
        _inspectorId: selectedSlotInspectorInfo._id,
        datetime: selectedDateTime,
        endtime: eventEndTime,
        inspectorName: `${selectedSlotInspectorInfo.firstName} ${selectedSlotInspectorInfo.lastName}`,
        name: `Quote Hold - ${address.street} ${address.city}`,
        description: `Hold created for quote until ${holdUntil.format('MM/DD/YYYY h:mm A')}`,
        expiresInMinutes: validExpiresInMinutes,
      };

      try {
        const holdEvent = await callServerAction<Event>({
          url: 'event',
          method: 'post',
          data: eventData,
          params: { _companyId: companyId },
          headers: { 'client-token': jwtToken },
        });
        holdEventId = holdEvent._id;
      } catch (eventError) {
        console.error('Failed to create hold event:', eventError);
        addToast({
          title: 'Warning',
          description: 'Quote will be sent without a time hold. You can add a hold later.',
          color: 'warning',
        });
        // Continue without hold - don't block quote sending
      }
    }

    // Build quote update payload - only fields that need updating
    // Draft quote already has charges and people from auto-save
    const quoteUpdateData: any = {
      status: 'pending',
      total: priceData.totalPrice,
      duration: priceData.totalDuration,
      ...(holdEventId && { _holdEventId: holdEventId }),
      ...(holdEventId && { holdDatetime: selectedDateTime || undefined }),
      ...(finalHoldUntil && { holdUntil: finalHoldUntil || undefined }),
      // Only save inspector if creating a hold
      ...(createHold && selectedSlotInspectorInfo
        ? { _inspectorId: [selectedSlotInspectorInfo._id] }
        : { _inspectorId: [] }),
      // If user filtered to a single inspector, they specifically requested them (only if creating hold)
      ...(createHold &&
        selectedInspectorIdsForFilter?.length === 1 && {
          _inspectorRequestedIds: selectedInspectorIdsForFilter,
        }),
      // Add bundle and discount code information if applicable
      ...(selectedBundle &&
        (() => {
          const bundle = bundles.find((b) => b._id === selectedBundle);
          return bundle
            ? {
                _bundleId: bundle._id,
                bundleName: bundle.name,
              }
            : {};
        })()),
      ...(appliedDiscountCode && {
        _discountCodeId: appliedDiscountCode._id,
        discountCodeName: appliedDiscountCode.code,
        appliedDiscountServiceIds: appliedDiscountServiceIds || [],
      }),
      // Add WhatConverts lead ID if available
      ...(wcLeadId && { wcLeadId }),
    };

    // If no hold was created, explicitly set hold fields to undefined
    if (!holdEventId) {
      quoteUpdateData._holdEventId = undefined;
      quoteUpdateData.holdDatetime = undefined;
      quoteUpdateData.holdUntil = undefined;
    }

    // Update draft quote to pending status using simple PATCH endpoint
    // Draft quote already contains charges and people from auto-save
    const updatedQuote = await callServerAction<Quote>({
      url: `quote/${draftQuoteId}`,
      method: 'patch',
      data: quoteUpdateData,
      params: { _companyId: companyId },
      headers: { 'client-token': jwtToken },
    });

    // CLEAR THE WORKING HOLD
    if (workingHold) {
      await callServerAction({
        url: `event/${workingHold._id}`,
        method: 'delete',
        params: { _companyId: companyId },
        headers: { 'client-token': jwtToken },
      }).catch((error) => {
        console.error('Error deleting working hold:', error);
      });
      setWorkingHold(null);
      setWorkingHoldExpiresAt(null);
    }

    // Clear draft from localStorage
    completeDraft(draftQuoteSlug || undefined);

    // Clear draft states
    setDraftQuoteId(null);
    setDraftQuoteSlug(null);

    // Generate access token for client portal redirect
    try {
      const accessToken = await generateAccessToken(
        {
          companyId: companyId,
          // Carry the client contact so portal actions (accept, edit services,
          // slot selection) are attributable to a specific contact.
          ...(contact?._id && { contactId: contact._id }),
        },
        '7d',
        'portal-access'
      );

      // Redirect to client portal quote page
      if (updatedQuote?.slug && typeof window !== 'undefined') {
        const portalUrl = `${process.env.NEXT_PUBLIC_CLIENT_URL}/quotes/${updatedQuote.slug}?access_token=${accessToken}`;
        window.location.href = portalUrl;
      } else {
        // Fallback: show success toast if redirect fails
        addToast({
          title: 'Quote Sent Successfully',
          description: 'The quote has been sent and is now pending.',
          color: 'success',
        });
      }
    } catch (tokenError) {
      console.error('Error generating access token:', tokenError);
      // Still show success toast even if token generation fails
      addToast({
        title: 'Quote Sent Successfully',
        description: 'The quote has been sent and is now pending.',
        color: 'success',
      });
    }

    return updatedQuote;
  } catch (error: any) {
    console.error('Error sending quote:', error);
    addToast({
      title: 'Error Sending Quote',
      description: error.message || 'An unexpected error occurred. Please try again.',
      color: 'danger',
    });
    throw error; // Re-throw so caller can handle if needed
  }
}
